import { useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { BookOpen, ArrowRight, HelpCircle } from "lucide-react";
import { getArticle, getArticlesBySection } from "@/data/articles";
import { articleImageAlts } from "@/data/articleImages";
import { PageHero } from "@/components/PageHero";
import pageCover from "@/assets/kupish/p-navodila-ustvarjalci.webp";

type NavodilaTag = "Priprava izdelka" | "Fotografije" | "Opisi & SEO" | "Marketing";
const NAVODILA_ORDER: NavodilaTag[] = [
  "Priprava izdelka",
  "Fotografije",
  "Opisi & SEO",
  "Marketing",
];
function classifyNavodila(slug: string, title: string): NavodilaTag {
  const s = `${slug} ${title}`;
  if (/fotograf|slik/i.test(s)) return "Fotografije";
  if (/besedil|seo|opis/i.test(s)) return "Opisi & SEO";
  if (/marketing|oglaš|promocij/i.test(s)) return "Marketing";
  return "Priprava izdelka";
}

export const Route = createFileRoute("/uporabniska-navodila-ustvarjalci")({
  head: () => {
    const title = "Navodila za prodajalce — kupish.si";
    const description =
      "Navodila za ponudnike na kupish.si: registracija in najem trgovine, urejanje profila, dodajanje izdelkov, fotografije, opisi, cene in obdelava naročil.";
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: "https://kupish.si/uporabniska-navodila-ustvarjalci" },
        { property: "og:type", content: "website" },
        { property: "og:locale", content: "sl_SI" },
        { property: "og:site_name", content: "kupish.si" },
        { name: "twitter:card", content: "summary" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description },
      ],
      links: [{ rel: "canonical", href: "https://kupish.si/uporabniska-navodila-ustvarjalci" }],
    };
  },
  component: Page,
});

const GUIDE_SLUGS = [
  "priprava-izdelka-za-splet",
  "optimizacija-fotografij-izdelka",
  "optimizacija-besedila",
  "seo-osnove-za-trgovce",
  "marketing-za-spletne-trgovine",
  "marketing-za-trgovce",
] as const;

const STEPS = [
  { title: "Registracija in plačilo najemnine", text: "Ustvarite račun ponudnika in poravnate letno najemnino, s čimer se aktivira nadzorna plošča." },
  { title: "Prijava in nadzorna plošča", text: "Prijava v račun ter pregled naročil, izdelkov, povpraševanj in obiskov trgovine." },
  { title: "Urejanje trgovine", text: "Ime trgovine, logotip, naslovna slika, opis, kontaktni podatki in pogoji dostave." },
  { title: "Dodajanje izdelkov", text: "Objava izdelka z več fotografijami, opisom, ceno, zalogo in uvrstitvijo v kategorije." },
  { title: "Obdelava naročil", text: "Prejem naročila po e-pošti, izdaja računa, odprema in obveščanje kupca." },
  { title: "Promocija in SEO", text: "Kako z boljšimi opisi, fotografijami in povezavami povečate vidnost izdelkov." },
];

const FAQ = [
  { q: "Ali potrebujem svojo spletno stran za prodajo?", a: "Ne. Trgovino uredite in vodite neposredno na kupish.si, brez stroškov postavitve in vzdrževanja lastne spletne trgovine." },
  { q: "Kako hitro bo moja trgovina aktivna po registraciji?", a: "Po plačilu letne najemnine se dostop aktivira samodejno, trgovina pa postane javno vidna običajno v 1–2 delovnih dneh." },
  { q: "Ali lahko kasneje spremenim ime trgovine ali logotip?", a: "Da. V nadzorni plošči lahko kadar koli posodobite ime trgovine, logotip, naslovno sliko in opis." },
  { q: "Kako dodam nove izdelke?", a: "V nadzorni plošči izberete Izdelki → Dodaj nov izdelek, vnesete podatke, fotografije, ceno in zalogo ter objavite. Izdelek se takoj prikaže v trgovini." },
  { q: "Koliko izdelkov lahko objavim?", a: "Letni najem vključuje objavo do 100 izdelkov." },
  { q: "Kdo določa ceno in pogoje dostave?", a: "Ponudnik. Cene, zaloge in pogoje dostave določate sami in jih kadar koli uredite." },
  { q: "Kdo izda račun in odgovarja za garancijo?", a: "Ponudnik kot prodajalec izda račun ter odgovarja za garancijo, vračila in reklamacije v skladu s slovensko zakonodajo." },
  { q: "Kako kupci stopijo v stik z menoj?", a: "Vsak izdelek in profil trgovine imata gumb za povpraševanje, prikazani pa so tudi vaši kontaktni podatki." },
];


function Page() {
  const guides = GUIDE_SLUGS
    .map((s) => getArticle(s))
    .filter((a): a is NonNullable<ReturnType<typeof getArticle>> => Boolean(a));
  // Fallback to whole section if any are missing
  const list = guides.length ? guides : getArticlesBySection("unikatne-vsebine");

  const tagged = list.map((a) => ({ a, tag: classifyNavodila(a.slug, a.title) }));
  const counts = NAVODILA_ORDER.reduce<Record<string, number>>((acc, t) => {
    acc[t] = tagged.filter((x) => x.tag === t).length;
    return acc;
  }, {});
  const availableTags = NAVODILA_ORDER.filter((t) => counts[t] > 0);
  const [activeTag, setActiveTag] = useState<NavodilaTag | null>(null);
  const filtered = useMemo(
    () => (activeTag ? tagged.filter((x) => x.tag === activeTag) : tagged),
    [tagged, activeTag],
  );

  return (
    <article className="pb-20">
      <PageHero src={pageCover} alt="Navodila za ponudnike na kupish.si — minimalističen 3D prikaz spletne trgovine" innerClassName="text-center">
        <div className="eyebrow justify-center inline-flex items-center gap-2">
          <BookOpen className="h-3.5 w-3.5" /> Priročnik za prodajalce
        </div>
        <h1 className="font-serif text-5xl md:text-6xl mt-4 leading-tight">
          Navodila za <span className=" text-accent">prodajalce</span>
        </h1>
        <p className="mt-6 text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
          Priročnik je namenjen ponudnikom, ki na kupish.si vodijo svojo spletno trgovino.
          Korak za korakom vas vodimo skozi postopke — od <strong className="text-foreground">registracije in plačila najemnine</strong>
          {" "}do <strong className="text-foreground">urejanja trgovine, objave izdelkov in obdelave naročil</strong>.
        </p>
      </PageHero>
      <div className="mx-auto max-w-5xl px-6 pt-4">

      {/* Značke */}
      {availableTags.length > 1 && (
        <div className="mt-10 flex flex-wrap justify-center gap-2">
          <button
            type="button"
            onClick={() => setActiveTag(null)}
            className={`px-3 py-1.5 text-xs uppercase tracking-wider rounded-full border transition ${
              activeTag === null
                ? "bg-accent text-accent-foreground border-accent"
                : "border-border hover:border-accent"
            }`}
          >
            Vsi vodniki ({tagged.length})
          </button>
          {availableTags.map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setActiveTag(t)}
              className={`px-3 py-1.5 text-xs uppercase tracking-wider rounded-full border transition ${
                activeTag === t
                  ? "bg-accent text-accent-foreground border-accent"
                  : "border-border hover:border-accent"
              }`}
            >
              {t} ({counts[t]})
            </button>
          ))}
        </div>
      )}

      {/* Article cards */}
      <div className="mt-10 grid md:grid-cols-2 gap-8">
        {filtered.map(({ a }) => (
          <Link
            key={a.slug}
            to="/clanek/$slug"
            params={{ slug: a.slug }}
            className="group block border border-border rounded-lg overflow-hidden hover:border-accent transition-colors bg-card"
          >
            {a.featuredImage && (
              <div className="aspect-[16/10] overflow-hidden bg-muted">
                <img
                  src={a.featuredImage}
                  alt={articleImageAlts[a.slug] ?? a.title}
                  title={a.title}
                  width={640}
                  height={400}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
                />
              </div>
            )}
            <div className="p-6">
              <h2 className="font-serif text-2xl leading-snug group-hover:text-accent text-foreground min-h-[3.4rem]">{a.title}</h2>
              <p className="mt-3 text-sm text-muted-foreground line-clamp-3">{a.excerpt}</p>
              <span className="mt-4 inline-flex items-center gap-1 text-xs uppercase tracking-wider text-accent">
                Preberi <ArrowRight className="h-3 w-3" />
              </span>
            </div>
          </Link>
        ))}
      </div>

      {/* Vsebina navodil */}
      <section className="mt-24">
        <h2 className="font-serif text-3xl md:text-4xl text-center">
          Vsebina <span className=" text-accent">navodil</span>
        </h2>
        <ol className="mt-10 grid sm:grid-cols-2 gap-5">
          {STEPS.map((s, i) => (
            <li
              key={s.title}
              className="flex gap-4 p-5 border border-border rounded-lg bg-card hover:border-accent transition-colors"
            >
              <span className="flex-shrink-0 h-9 w-9 rounded-full bg-accent/10 text-accent flex items-center justify-center font-serif text-lg">
                {i + 1}
              </span>
              <div>
                <h3 className="font-serif text-lg leading-snug">{s.title}</h3>
                <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed">{s.text}</p>
              </div>
            </li>
          ))}
        </ol>
      </section>

      {/* FAQ */}
      <section className="mt-24">
        <div className="text-center">
          <div className="eyebrow justify-center inline-flex items-center gap-2">
            <HelpCircle className="h-3.5 w-3.5" /> FAQ
          </div>
          <h2 className="font-serif text-3xl md:text-4xl mt-3">
            Pogosta <span className=" text-accent">vprašanja</span> in odgovori
          </h2>
        </div>
        <div className="mt-10 divide-y divide-border border-y border-border">
          {FAQ.map((f, i) => (
            <details key={i} className="group py-5">
              <summary className="flex cursor-pointer items-start justify-between gap-4 list-none">
                <span className="font-serif text-lg leading-snug">{f.q}</span>
                <span className="mt-1 text-accent text-xl leading-none transition-transform group-open:rotate-45">+</span>
              </summary>
              <p className="mt-3 text-muted-foreground leading-relaxed">{f.a}</p>
            </details>
          ))}
        </div>
      </section>
      </div>
    </article>
  );
}
